import { BasicButton, GhostButton } from '../../../../../../widgets/buttons';
import { ColorPicker } from '../../../../../../widgets/colorPicker';
import { useDropdown } from '../../../../../../widgets/hooks/useDropdown';
import { Icon } from '../../../../../../widgets/icon';
import { Spinner } from '../../../../../../widgets/spinner';
import { debounce } from 'lodash';
import React from 'react';
import { showErrorNotification } from 'src/components/widgets/notificationToaster';
import DeleteIcon from 'src/shared/material_icons/action/delete.svg';
import UploadIcon from 'src/shared/material_icons/file/upload.svg';
import ImageIcon from 'src/shared/material_icons/image/image.svg';
import PaletteIcon from 'src/shared/material_icons/image/palette.svg';
import { T, uploadComposerPreviewImage } from 'src/shared/helpers';
import styled from 'styled-components';

interface ComposerUploadComponentProps {
    passedImgUrl?: string;
    onImagePick: (imgUrl: string, tempImageFileKey?: string) => void | string | Promise<void | string>;
}

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
const DEFAULT_COLOR = '#ffffff';
const COLOR_IMAGE_SIZE = 512;

const UploadBox = styled.div<{ $dragging: boolean }>`
    position: relative;
    padding: 12px;
    border: 1px dashed
        ${(props) => (props.$dragging ? props.theme.app.component.checkbox.selected.active.border : props.theme.app.component.checkbox.unselected.active.border)};
    border-radius: 8px;
    background: ${(props) => (props.$dragging ? props.theme.app.component.tabs.active.backgroundColor : props.theme.app.backgroundColor)};
    transition:
        background 0.2s ease,
        border-color 0.2s ease;
`;

const ImageRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
`;

const Thumbnail = styled.button`
    display: flex;
    flex: 0 0 72px;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 72px;
    padding: 0;
    overflow: hidden;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 6px;
    background: ${(props) => props.theme.app.pageBackgroundColor};
    color: ${(props) => props.theme.app.component.checkbox.unselected.active.border};
    cursor: pointer;

    > img {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }

    ${Icon}, ${Icon} svg {
        width: 28px;
        height: 28px;
    }
`;

const ImageDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
`;

const Title = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${(props) => props.theme.app.textColor};
`;

const Hint = styled.span`
    font-size: 12px;
    color: ${(props) => props.theme.app.primary};
`;

const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
    margin-top: 6px;
`;

const LoadingOverlay = styled.div`
    position: absolute;
    inset: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.8);
`;

const HiddenFileInput = styled.input`
    display: none;
`;

const createColorImage = async (color: string) => {
    const canvas = document.createElement('canvas');
    canvas.width = COLOR_IMAGE_SIZE;
    canvas.height = COLOR_IMAGE_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Unable to create the selected color image.');

    ctx.fillStyle = color;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((value) => (value ? resolve(value) : reject(new Error('Unable to create the selected color image.'))));
    });
    return new File([blob], `color_image_${Date.now()}.png`, { type: 'image/png' });
};

const ComposerUploadComponent: React.FC<ComposerUploadComponentProps> = ({ passedImgUrl, onImagePick }) => {
    const [currentImage, setCurrentImage] = React.useState<string | undefined>(passedImgUrl);
    const [isDragging, setIsDragging] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [selectedColor, setSelectedColor] = React.useState<string>(DEFAULT_COLOR);
    const [openColorPicker, , isColorPickerOpen, ColorPickerDropdown] = useDropdown();
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    // The colour picker upload is debounced once, so it must always reach the latest callback:
    // in the side panels the same component instance moves from one option to the next.
    const onImagePickRef = React.useRef(onImagePick);
    onImagePickRef.current = onImagePick;

    React.useEffect(() => {
        setCurrentImage(passedImgUrl);
    }, [passedImgUrl]);

    const uploadFile = async (file: File) => {
        setLoading(true);
        try {
            const image = await uploadComposerPreviewImage(file);
            const finalImageUrl = await onImagePickRef.current(image.url, image.key);
            setCurrentImage(finalImageUrl || image.url);
        } catch (error) {
            console.error('Error uploading image:', error);
            showErrorNotification({ message: T._('The preview image could not be uploaded. Try again.', 'Admin') });
        } finally {
            setLoading(false);
        }
    };

    const handleFile = (file: File | undefined) => {
        if (!file) return;
        if (!ACCEPTED_TYPES.includes(file.type)) {
            showErrorNotification({ message: T._('Please upload a valid image file (PNG, JPG, JPEG, WEBP)', 'Admin') });
            return;
        }
        setSelectedColor(DEFAULT_COLOR);
        void uploadFile(file);
    };

    const uploadColor = React.useMemo(
        () =>
            debounce(async (color: string) => {
                try {
                    await uploadFile(await createColorImage(color));
                } catch (error) {
                    console.error('Error creating color image:', error);
                    showErrorNotification({ message: T._('The preview image could not be uploaded. Try again.', 'Admin') });
                }
            }, 300),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    React.useEffect(() => () => uploadColor.cancel(), [uploadColor]);

    const handleRemoveImage = async () => {
        await onImagePickRef.current('');
        setCurrentImage(undefined);
        setSelectedColor(DEFAULT_COLOR);
    };

    const browse = () => fileInputRef.current?.click();

    return (
        <UploadBox
            $dragging={isDragging}
            onDragEnter={(e) => {
                e.preventDefault();
                setIsDragging(true);
            }}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={(e) => {
                e.preventDefault();
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setIsDragging(false);
            }}
            onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                handleFile(e.dataTransfer.files[0]);
            }}
        >
            {loading && (
                <LoadingOverlay>
                    <Spinner />
                </LoadingOverlay>
            )}
            <ImageRow>
                <Thumbnail type='button' aria-label={currentImage ? T._('Replace image', 'Admin') : T._('Upload image', 'Admin')} onClick={browse}>
                    {currentImage ? (
                        <img src={currentImage} alt={T._('Preview image', 'Admin')} />
                    ) : (
                        <Icon>
                            <ImageIcon />
                        </Icon>
                    )}
                </Thumbnail>
                <ImageDetails>
                    <Title>{T._('Drag and drop an image here', 'Admin')}</Title>
                    <Hint>{T._('Supported formats: PNG, JPG, JPEG, WEBP', 'Admin')}</Hint>
                    <Actions>
                        <BasicButton type='button' $size='small' onClick={browse}>
                            <UploadIcon />
                            {currentImage ? T._('Replace', 'Admin') : T._('Upload image', 'Admin')}
                        </BasicButton>
                        <BasicButton type='button' $size='small' onClick={(e) => openColorPicker(e.currentTarget, 'bottom-start')}>
                            <PaletteIcon />
                            {T._('Color image', 'Admin')}
                        </BasicButton>
                        {currentImage && (
                            <GhostButton type='button' $size='small' onClick={handleRemoveImage}>
                                <DeleteIcon />
                                {T._('Remove', 'Admin')}
                            </GhostButton>
                        )}
                        {isColorPickerOpen && (
                            <ColorPickerDropdown>
                                <ColorPicker
                                    color={selectedColor}
                                    onChange={(color) => {
                                        setSelectedColor(color);
                                        uploadColor(color);
                                    }}
                                />
                            </ColorPickerDropdown>
                        )}
                    </Actions>
                </ImageDetails>
            </ImageRow>
            <HiddenFileInput
                ref={fileInputRef}
                type='file'
                accept={ACCEPTED_TYPES.join(',')}
                onChange={(e) => {
                    handleFile(e.target.files?.[0]);
                    e.target.value = '';
                }}
            />
        </UploadBox>
    );
};

export default ComposerUploadComponent;
